/* eslint-disable warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { service } from '@ember/service';
import { action } from '@ember/object';
import LogLevel from 'linced-admin/enums/log-level';
import config from 'linced-admin/config/environment';

export default class TaskController extends Controller {
  @action
  setDeleteTaskDialogOpen(value) {
    this.deleteTaskDialogOpen = value;
  }

  @action
  setManualEndDate(value) {
    this.manualEndDate = value;
  }

  @action
  setManualSchoolId(value) {
    this.manualSchoolId = value;
  }

  @action
  setManualStartDate(value) {
    this.manualStartDate = value;
  }

  // Attrs
  LogLevel = LogLevel;

  // Services
  @service
  fetch;

  @service
  paperToaster;

  @service
  router;

  @service
  store;

  // State
  // deleteInProgress provides state for if a delete request is currently
  // in flight.
  @tracked
  deleteInProgress = false;

  // deleteTaskDialogOpen provides the state of the task delete dialog.
  @tracked
  deleteTaskDialogOpen = false;

  // taskRunInProgress provides state for if a task run request is currently in
  // flight.
  @tracked
  taskRunInProgress = false;

  // Manual task params for recalculateNzMoeDaysManual.
  @tracked manualSchoolId = '';

  @tracked manualStartDate = '';
  @tracked manualEndDate = '';
  @tracked
  manualDateError = null;

  @tracked
  taskDeleteDialogOpen;

  // Computed
  get manualFieldsComplete() {
    return (
      !!this.manualSchoolId && !!this.manualStartDate && !!this.manualEndDate
    );
  }

  // Methods
  /**
   * toastMessage pops a notification of success or failure.
   *
   * @param {boolean} success - Whether a success was had or not.
   * @param {string} message - The message to display
   */
  toastMessage(success, message) {
    let toastClass = 'success';
    if (!success) {
      toastClass = 'warning';
    }

    this.paperToaster.show(message, {
      duration: 6000,
      position: 'top right',
      toastClass: toastClass,
    });
  }

  // reset provides a call for the router to reset the controller on
  // re-entering the route.
  reset() {
    this.deleteInProgress = false;
    this.deleteTaskDialogOpen = false;
    this.taskRunInProgress = false;
    this.manualSchoolId = '';
    this.manualStartDate = '';
    this.manualEndDate = '';
    this.manualDateError = null;
  }

  /**
   * deleteTask deletes the provided scheduled task from the server.
   *
   * @param {Object} task - task record.
   */
  @action
  deleteTask(task) {
    this.deleteInProgress = true;
    task
      .destroyRecord()
      .then(() => {
        this.toastMessage(
          true,
          `Deleted ${task.serviceName} ${task.serviceTask}`,
        );
      })
      .then(() => {
        this.router.transitionTo('cron');
      })
      .catch((err) => {
        this.toastMessage(
          false,
          `Error deleting ${task.serviceName}: ${task.serviceTask}. Error: ${err}`,
        );
      })
      .finally(() => {
        this.deleteInProgress = false;
        this.taskDeleteDialogOpen = false;
      });
  }

  /**
   * runManualTask sets the task description to the provided params as JSON
   * then fires a request to run the task.
   *
   * @param {Object} task - task record.
   */
  @action
  runManualTask(task) {
    const schoolId = this.manualSchoolId;
    const startDate = this.manualStartDate;
    const endDate = this.manualEndDate;

    if (!schoolId || !startDate || !endDate) {
      this.manualDateError = 'All fields are required.';
      return;
    }
    if (startDate > endDate) {
      this.manualDateError = 'Start date must be before or equal to end date.';
      return;
    }

    this.manualDateError = null;
    this.taskRunInProgress = true;

    task.set('description', JSON.stringify({ schoolId, startDate, endDate }));

    let url = `${config.APP.apiHost}/cron/${config.APP.apiPath}/tasks/${task.id}/:run`;
    return task
      .save()
      .then(() => this.fetch.post(url))
      .then(() => {
        this.toastMessage(
          true,
          `Running ${task.serviceName} ${task.serviceTask}!`,
        );
      })
      .catch((err) => {
        this.toastMessage(
          false,
          `Error running ${task.serviceName}: ${task.serviceTask}. Error: ${err}`,
        );
      })
      .finally(() => {
        task.set('description', '');
        task.save();
        this.model.logs.update();
        this.taskRunInProgress = false;
      });
  }

  /**
   * runTask fires a request to run the task now.
   *
   * @param {Object} task - task record.
   */
  @action
  runTask(task) {
    this.taskRunInProgress = true;

    let url = `${config.APP.apiHost}/cron/${config.APP.apiPath}/tasks/${task.id}/:run`;
    return this.fetch
      .post(url)
      .then(() => {
        this.toastMessage(
          true,
          `Running ${task.serviceName} ${task.serviceTask}!`,
        );
      })
      .catch((err) => {
        this.toastMessage(
          false,
          `Error running ${task.serviceName}: ${task.serviceTask}. Error: ${err}`,
        );
      })
      .finally(() => {
        this.model.logs.update();
        this.taskRunInProgress = false;
      });
  }

  /**
   * toggleEnabled flips the enabled state and instantly saves the task.
   *
   * @param {Object} task - task record.
   */
  @action
  toggleEnabled(task) {
    // store the toggled state.
    let toggledEnabledState = !task.enabled;

    // build a state string to return the action performed on save.
    let state = 'disabl';
    if (toggledEnabledState) {
      state = 'enabl';
    }

    // Update state and notify of outcome.
    task.set('enabled', toggledEnabledState);
    task
      .save()
      .then(() => {
        this.toastMessage(
          true,
          `${state}ed ${task.serviceName}'s ${task.serviceTask}`,
        );
      })
      .catch((err) => {
        this.toastMessage(
          false,
          `Error ${state}ing ${task.serviceName} ${task.serviceTask}. Error: ${err}`,
        );
      });
  }
}
